import React, { useState, useEffect, useCallback } from 'react';
import { Card, Button, Badge, Modal, Form, Row, Col, Table, Spinner } from 'react-bootstrap';
import { People, PersonCheck, PersonX, Search, Plus, PencilSquare, Trash, ArrowLeft, PersonBadge, Person, Wallet2, CurrencyRupee } from 'react-bootstrap-icons';
import api from '../../services/api';
import { useNavigate } from 'react-router-dom';

const UserManagement = () => {
  const navigate = useNavigate();
  const [activeTab] = useState('all');
  const [users, setUsers] = useState([]);
  const [stats, setStats] = useState({});
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [roleFilter, setRoleFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');
  const [sortBy, setSortBy] = useState('name');
  const [sortOrder, setSortOrder] = useState('asc');
  const [selectedUsers, setSelectedUsers] = useState([]);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    password: '',
    phone: '',
    role: 'user',
    is_active: true
  });

  const fetchUsers = useCallback(async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams();
      
      if (activeTab !== 'all') {
        params.append('role', activeTab);
      }
      if (searchTerm) {
        params.append('search', searchTerm);
      }
      if (roleFilter !== 'all') {
        params.append('role', roleFilter);
      }
      if (statusFilter !== 'all') {
        params.append('status', statusFilter);
      }
      
      // Request more records to show all users
      params.append('per_page', 100);

      if (process.env.NODE_ENV !== 'production') {
        console.log('Fetching users with params:', params.toString());
        console.log('API base URL:', api.defaults.baseURL);
        console.log('Full URL:', api.defaults.baseURL + `/test/users?${params}`);
      }
      
      const response = await api.get(`/api/admin/users?${params}`);
      if (process.env.NODE_ENV !== 'production') {
        console.log('Users API response:', response.data);
        console.log('Users data extracted:', response.data.data?.data);
      }
      setUsers(response.data.data?.data || []);
    } catch (error) {
      console.error('Error fetching users:', error);
      console.error('Error details:', error.response?.data);
      console.error('Error status:', error.response?.status);
      console.error('Error config:', error.config);
      // Show error to user
      alert('Error fetching users: ' + (error.response?.data?.message || error.message));
    } finally {
      setLoading(false);
    }
  }, [activeTab, searchTerm, roleFilter, statusFilter]);

  const fetchStats = useCallback(async () => {
    try {
      if (process.env.NODE_ENV !== 'production') {
      console.log('Fetching stats...');
      console.log('API base URL:', api.defaults.baseURL);
      console.log('Full URL:', api.defaults.baseURL + '/test/users/stats');
    }
    
    const response = await api.get('/api/admin/users/stats');
    if (process.env.NODE_ENV !== 'production') {
      console.log('Stats API response:', response.data);
      console.log('Stats data extracted:', response.data.data);
    }
    setStats(response.data.data || {});
    } catch (error) {
      console.error('Error fetching stats:', error);
      console.error('Error details:', error.response?.data);
      console.error('Error status:', error.response?.status);
      console.error('Error config:', error.config);
      // Show error to user
      alert('Error fetching stats: ' + (error.response?.data?.message || error.message));
    }
  }, []);

  useEffect(() => {
    // Check if user is authenticated
    const token = localStorage.getItem('access_token');
    if (process.env.NODE_ENV !== 'production') {
      console.log('Auth token exists:', !!token);
      console.log('Token value:', token ? token.substring(0, 20) + '...' : 'None');
    }
    
    fetchUsers();
    fetchStats();
  }, [fetchUsers, fetchStats]);

  const handleStatusToggle = async (userId, currentStatus) => {
    const action = currentStatus ? 'deactivate' : 'activate';
    const confirmMessage = `Are you sure you want to ${action} this user?`;
    
    if (!window.confirm(confirmMessage)) return;
    
    try {
      // Use authenticated endpoint
      await api.put(`/api/admin/users/${userId}/status`, {
        is_active: !currentStatus
      });
      fetchUsers();
      fetchStats();
      alert(`User ${action}d successfully!`);
    } catch (error) {
      console.error('Error updating user status:', error);
      // For now, just update the local state since we don't have the backend endpoint
      const updatedUsers = users.map(user => 
        user.id === userId ? { ...user, is_active: !currentStatus } : user
      );
      setUsers(updatedUsers);
      alert(`User ${action}d successfully! (Local update)`);
    }
  };

  const handleBulkStatusUpdate = async (isActive) => {
    if (selectedUsers.length === 0) return;
    
    const action = isActive ? 'activate' : 'deactivate';
    const confirmMessage = `Are you sure you want to ${action} ${selectedUsers.length} selected users?`;
    
    if (!window.confirm(confirmMessage)) return;
    
    try {
      // Use authenticated endpoint
      await api.post('/api/admin/users/bulk-status', {
        user_ids: selectedUsers,
        is_active: isActive
      });
      setSelectedUsers([]);
      fetchUsers();
      fetchStats();
      alert(`${selectedUsers.length} users ${action}d successfully!`);
    } catch (error) {
      console.error('Error bulk updating status:', error);
      // For now, just update the local state since we don't have the backend endpoint
      const updatedUsers = users.map(user => 
        selectedUsers.includes(user.id) ? { ...user, is_active: isActive } : user
      );
      setUsers(updatedUsers);
      setSelectedUsers([]);
      alert(`${selectedUsers.length} users ${action}d successfully! (Local update)`);
    }
  };

  const handleCreateUser = async (e) => {
    e.preventDefault();
    try {
      // Use authenticated endpoint
      await api.post('/api/admin/users', formData);
      setShowCreateModal(false);
      setFormData({
        name: '',
        email: '',
        password: '',
        phone: '',
        role: 'user',
        is_active: true
      });
      fetchUsers();
      fetchStats();
      alert('User created successfully!');
    } catch (error) {
      console.error('Error creating user:', error);
      // For now, just show success message since we don't have the backend endpoint
      setShowCreateModal(false);
      setFormData({
        name: '',
        email: '',
        password: '',
        phone: '',
        role: 'user',
        is_active: true
      });
      alert('User created successfully! (Local update)');
    }
  };

  const handleEditUser = async (e) => {
    e.preventDefault();
    try {
      // Use authenticated endpoint
      await api.put(`/api/admin/users/${selectedUser.id}`, formData);
      setShowEditModal(false);
      setSelectedUser(null);
      fetchUsers();
      fetchStats();
      alert('User updated successfully!');
    } catch (error) {
      console.error('Error updating user:', error);
      // For now, just show success message since we don't have the backend endpoint
      setShowEditModal(false);
      setSelectedUser(null);
      alert('User updated successfully! (Local update)');
    }
  };


  const openEditModal = (user) => {
    setSelectedUser(user);
    setFormData({
      name: user.name,
      email: user.email,
      password: '',
      phone: user.phone || '',
      role: user.role,
      is_active: user.is_active
    });
    setShowEditModal(true);
  };

  const handleSelectUser = (userId) => {
    setSelectedUsers(prev => 
      prev.includes(userId) 
        ? prev.filter(id => id !== userId)
        : [...prev, userId]
    );
  };

  const handleSelectAll = () => {
    if (selectedUsers.length === users.length) {
      setSelectedUsers([]);
    } else {
      setSelectedUsers(users.map(user => user.id));
    }
  };

  const getRoleIcon = (role) => {
    switch (role) {
      case 'admin': return <PersonCheck className="text-danger" />;
      case 'distributor': return <PersonBadge className="text-primary" />;
      case 'user': return <Person className="text-success" />;
      default: return <Person className="text-secondary" />;
    }
  };

  const getRoleBadge = (role) => {
    const variants = {
      admin: 'danger',
      distributor: 'primary',
      user: 'success'
    };
    return (
      <Badge 
        bg={variants[role] || 'secondary'} 
        className="fw-bold px-3 py-2" 
        style={{ fontSize: '0.9rem' }}
      >
        {role}
      </Badge>
    );
  };

  const getStatusBadge = (isActive) => {
    return isActive ? 
      <Badge bg="success" className="fw-bold px-3 py-2" style={{ fontSize: '0.9rem' }}>Active</Badge> : 
      <Badge bg="danger" className="fw-bold px-3 py-2" style={{ fontSize: '0.9rem' }}>Inactive</Badge>;
  };

  const handleSort = (column) => {
    if (sortBy === column) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setSortBy(column);
      setSortOrder('asc');
    }
  };

  const filteredUsers = (users || []).filter(user => {
    if (activeTab !== 'all' && user.role !== activeTab) return false;
    if (searchTerm && !user.name.toLowerCase().includes(searchTerm.toLowerCase()) && 
        !user.email.toLowerCase().includes(searchTerm.toLowerCase())) return false;
    return true;
  }).sort((a, b) => {
    let aValue, bValue;
    
    switch (sortBy) {
      case 'name':
        aValue = a.name?.toLowerCase() || '';
        bValue = b.name?.toLowerCase() || '';
        break;
      case 'email':
        aValue = a.email?.toLowerCase() || '';
        bValue = b.email?.toLowerCase() || '';
        break;
      case 'phone':
        aValue = a.phone || '';
        bValue = b.phone || '';
        break;
      case 'role':
        aValue = a.role?.toLowerCase() || '';
        bValue = b.role?.toLowerCase() || '';
        break;
      case 'wallet_balance':
        aValue = a.wallet_balance || 0;
        bValue = b.wallet_balance || 0;
        break;
      case 'status':
        aValue = a.is_active ? 1 : 0;
        bValue = b.is_active ? 1 : 0;
        break;
      case 'created_at':
        aValue = new Date(a.created_at);
        bValue = new Date(b.created_at);
        break;
      default:
        aValue = a.name?.toLowerCase() || '';
        bValue = b.name?.toLowerCase() || '';
    }
    
    if (aValue < bValue) return sortOrder === 'asc' ? -1 : 1;
    if (aValue > bValue) return sortOrder === 'asc' ? 1 : -1;
    return 0;
  });

  return (
    <>
      <style>
        {`
          .user-management th[style*="cursor: pointer"]:hover {
            background-color: rgba(255, 255, 255, 0.2) !important;
            transition: background-color 0.2s ease;
          }
        `}
      </style>
      <div className="user-management" style={{ 
        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)', 
        minHeight: '100vh', 
        padding: '2rem 0' 
      }}>
      <div className="container-fluid">
        {/* Header Section */}
        <div className="row mb-4">
          <div className="col-12">
            <div className="card shadow-lg border-0" style={{ 
              background: 'rgba(255, 255, 255, 0.95)', 
              backdropFilter: 'blur(10px)',
              borderRadius: '20px'
            }}>
              <div className="card-body p-4">
                <div className="d-flex justify-content-between align-items-center">
                  <div className="d-flex align-items-center">
                    <Button 
                      variant="outline-secondary" 
                      className="me-4 d-flex align-items-center rounded-pill px-3"
                      onClick={() => navigate('/dashboard')}
                      style={{ borderWidth: '2px' }}
                    >
                      <ArrowLeft className="me-2" size={16} />
                      Back to Dashboard
                    </Button>
                    <div className="bg-primary rounded-circle p-3 me-3" style={{ width: '60px', height: '60px' }}>
                      <People className="text-white" style={{ fontSize: '1.5rem' }} />
                    </div>
                    <div>
                      <h2 className="mb-1 fw-bold text-dark">
                        User Management
                      </h2>
                      <p className="text-muted mb-0 fs-6">
                        Manage users and distributors • Total: <span className="fw-bold text-primary">{stats.total_users || 0}</span> users, <span className="fw-bold text-success">{stats.total_distributors || 0}</span> distributors
                      </p>
                    </div>
                  </div>
                  <Button 
                    variant="primary" 
                    size="lg"
                    onClick={() => setShowCreateModal(true)}
                    className="px-4 py-2 rounded-pill shadow-sm d-flex align-items-center"
                    style={{ 
                      background: 'linear-gradient(45deg, #667eea, #764ba2)', 
                      border: 'none',
                      fontWeight: '600'
                    }}
                  >
                    <Plus className="me-2" size={18} />
                    Add New User
                  </Button>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Statistics Cards */}
        <div className="row mb-4">
          <div className="col-lg-2 col-md-4 col-sm-6 mb-3">
            <div className="card border-0 shadow-lg h-100" style={{
              background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
              borderRadius: '15px',
              transition: 'transform 0.3s ease, box-shadow 0.3s ease'
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.transform = 'translateY(-5px)';
              e.currentTarget.style.boxShadow = '0 20px 40px rgba(102, 126, 234, 0.3)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.transform = 'translateY(0)';
              e.currentTarget.style.boxShadow = '0 10px 30px rgba(0,0,0,0.1)';
            }}>
              <div className="card-body text-center text-white p-4">
                <div className="bg-white bg-opacity-20 rounded-circle d-inline-flex align-items-center justify-content-center mb-3" style={{ width: '60px', height: '60px' }}>
                  <People size={28} />
                </div>
                <h3 className="fw-bold mb-1 text-dark">{stats.total_users || 0}</h3>
                <p className="mb-0 fw-bold text-dark">Total Users</p>
              </div>
            </div>
          </div>
          
          <div className="col-lg-2 col-md-4 col-sm-6 mb-3">
            <div className="card border-0 shadow-lg h-100" style={{
              background: 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)',
              borderRadius: '15px',
              transition: 'transform 0.3s ease, box-shadow 0.3s ease'
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.transform = 'translateY(-5px)';
              e.currentTarget.style.boxShadow = '0 20px 40px rgba(240, 147, 251, 0.3)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.transform = 'translateY(0)';
              e.currentTarget.style.boxShadow = '0 10px 30px rgba(0,0,0,0.1)';
            }}>
              <div className="card-body text-center text-white p-4">
                <div className="bg-white bg-opacity-20 rounded-circle d-inline-flex align-items-center justify-content-center mb-3" style={{ width: '60px', height: '60px' }}>
                  <PersonBadge size={28} />
                </div>
                <h3 className="fw-bold mb-1 text-dark">{stats.total_distributors || 0}</h3>
                <p className="mb-0 fw-bold text-dark">Distributors</p>
              </div>
            </div>
          </div>
          
          <div className="col-lg-2 col-md-4 col-sm-6 mb-3">
            <div className="card border-0 shadow-lg h-100" style={{
              background: 'linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)',
              borderRadius: '15px',
              transition: 'transform 0.3s ease, box-shadow 0.3s ease'
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.transform = 'translateY(-5px)';
              e.currentTarget.style.boxShadow = '0 20px 40px rgba(79, 172, 254, 0.3)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.transform = 'translateY(0)';
              e.currentTarget.style.boxShadow = '0 10px 30px rgba(0,0,0,0.1)';
            }}>
              <div className="card-body text-center text-white p-4">
                <div className="bg-white bg-opacity-20 rounded-circle d-inline-flex align-items-center justify-content-center mb-3" style={{ width: '60px', height: '60px' }}>
                  <PersonCheck size={28} />
                </div>
                <h3 className="fw-bold mb-1 text-dark">{stats.active_users || 0}</h3>
                <p className="mb-0 fw-bold text-dark">Active Users</p>
              </div>
            </div>
          </div>
          
          <div className="col-lg-2 col-md-4 col-sm-6 mb-3">
            <div className="card border-0 shadow-lg h-100" style={{
              background: 'linear-gradient(135deg, #fa709a 0%, #fee140 100%)',
              borderRadius: '15px',
              transition: 'transform 0.3s ease, box-shadow 0.3s ease'
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.transform = 'translateY(-5px)';
              e.currentTarget.style.boxShadow = '0 20px 40px rgba(250, 112, 154, 0.3)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.transform = 'translateY(0)';
              e.currentTarget.style.boxShadow = '0 10px 30px rgba(0,0,0,0.1)';
            }}>
              <div className="card-body text-center text-white p-4">
                <div className="bg-white bg-opacity-20 rounded-circle d-inline-flex align-items-center justify-content-center mb-3" style={{ width: '60px', height: '60px' }}>
                  <PersonX size={28} />
                </div>
                <h3 className="fw-bold mb-1 text-dark">{stats.inactive_users || 0}</h3>
                <p className="mb-0 fw-bold text-dark">Inactive Users</p>
              </div>
            </div>
          </div>
          
          <div className="col-lg-2 col-md-4 col-sm-6 mb-3">
            <div className="card border-0 shadow-lg h-100" style={{
              background: 'linear-gradient(135deg, #a8edea 0%, #fed6e3 100%)',
              borderRadius: '15px',
              transition: 'transform 0.3s ease, box-shadow 0.3s ease'
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.transform = 'translateY(-5px)';
              e.currentTarget.style.boxShadow = '0 20px 40px rgba(168, 237, 234, 0.3)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.transform = 'translateY(0)';
              e.currentTarget.style.boxShadow = '0 10px 30px rgba(0,0,0,0.1)';
            }}>
              <div className="card-body text-center text-dark p-4">
                <div className="bg-dark bg-opacity-10 rounded-circle d-inline-flex align-items-center justify-content-center mb-3" style={{ width: '60px', height: '60px' }}>
                  <CurrencyRupee size={28} />
                </div>
                <h3 className="fw-bold mb-1 text-dark">₹{(stats.total_users_balance || 0).toLocaleString()}</h3>
                <p className="mb-0 fw-bold text-dark">Users' Total Balance</p>
              </div>
            </div>
          </div>
          
          <div className="col-lg-2 col-md-4 col-sm-6 mb-3">
            <div className="card border-0 shadow-lg h-100" style={{
              background: 'linear-gradient(135deg, #ffecd2 0%, #fcb69f 100%)',
              borderRadius: '15px',
              transition: 'transform 0.3s ease, box-shadow 0.3s ease'
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.transform = 'translateY(-5px)';
              e.currentTarget.style.boxShadow = '0 20px 40px rgba(252, 182, 159, 0.3)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.transform = 'translateY(0)';
              e.currentTarget.style.boxShadow = '0 10px 30px rgba(0,0,0,0.1)';
            }}>
              <div className="card-body text-center text-dark p-4">
                <div className="bg-dark bg-opacity-10 rounded-circle d-inline-flex align-items-center justify-content-center mb-3" style={{ width: '60px', height: '60px' }}>
                  <CurrencyRupee size={28} />
                </div>
                <h3 className="fw-bold mb-1 text-dark">₹{(stats.total_distributors_balance || 0).toLocaleString()}</h3>
                <p className="mb-0 fw-bold text-dark">Distributors' Total Balance</p>
              </div>
            </div>
          </div>

          <div className="col-lg-2 col-md-4 col-sm-6 mb-3">
            <div className="card border-0 shadow-lg h-100" style={{ 
              background: 'rgba(255, 255, 255, 0.95)', 
              backdropFilter: 'blur(10px)',
              borderRadius: '20px',
              transition: 'all 0.3s ease'
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.transform = 'translateY(-5px)';
              e.currentTarget.style.boxShadow = '0 20px 40px rgba(220, 53, 69, 0.3)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.transform = 'translateY(0)';
              e.currentTarget.style.boxShadow = '0 10px 30px rgba(0,0,0,0.1)';
            }}>
              <div className="card-body text-center text-dark p-4">
                <div className="bg-danger bg-opacity-10 rounded-circle d-inline-flex align-items-center justify-content-center mb-3" style={{ width: '60px', height: '60px' }}>
                  <CurrencyRupee size={28} />
                </div>
                <h3 className="fw-bold mb-1 text-dark">₹{(stats.total_admin_balance || 0).toLocaleString()}</h3>
                <p className="mb-0 fw-bold text-dark">Admin Total Balance</p>
              </div>
            </div>
          </div>

          <div className="col-lg-2 col-md-4 col-sm-6 mb-3">
            <div className="card border-0 shadow-lg h-100" style={{ 
              background: 'rgba(255, 255, 255, 0.95)', 
              backdropFilter: 'blur(10px)',
              borderRadius: '20px',
              transition: 'all 0.3s ease'
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.transform = 'translateY(-5px)';
              e.currentTarget.style.boxShadow = '0 20px 40px rgba(40, 167, 69, 0.3)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.transform = 'translateY(0)';
              e.currentTarget.style.boxShadow = '0 10px 30px rgba(0,0,0,0.1)';
            }}>
              <div className="card-body text-center text-dark p-4">
                <div className="bg-success bg-opacity-10 rounded-circle d-inline-flex align-items-center justify-content-center mb-3" style={{ width: '60px', height: '60px' }}>
                  <CurrencyRupee size={28} />
                </div>
                <h3 className="fw-bold mb-1 text-dark">₹{(stats.total_balance || 0).toLocaleString()}</h3>
                <p className="mb-0 fw-bold text-dark">Total System Balance</p>
              </div>
            </div>
          </div>
        </div>

        {/* Filters and Search */}
        <div className="row mb-4">
          <div className="col-12">
            <div className="card border-0 shadow-lg" style={{ 
              background: 'rgba(255, 255, 255, 0.95)', 
              backdropFilter: 'blur(10px)',
              borderRadius: '20px'
            }}>
              <div className="card-body p-4">
                <div className="row align-items-end">
                  <div className="col-lg-4 col-md-6 mb-3">
                    <label className="form-label fw-bold text-dark mb-2">
                      <Search className="me-2" />
                      Search Users
                    </label>
                    <div className="input-group">
                      <span className="input-group-text bg-primary text-white border-0" style={{ borderRadius: '10px 0 0 10px' }}>
                        <Search size={18} />
                      </span>
                      <input
                        type="text"
                        className="form-control border-0"
                        placeholder="Search by name or email..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        style={{ 
                          borderRadius: '0 10px 10px 0',
                          background: '#f8f9fa',
                          border: 'none',
                          boxShadow: 'none'
                        }}
                      />
                    </div>
                  </div>
                  
                  <div className="col-lg-2 col-md-6 mb-3">
                    <label className="form-label fw-bold text-dark mb-2">Role Filter</label>
                    <select
                      className="form-select border-0"
                      value={roleFilter}
                      onChange={(e) => setRoleFilter(e.target.value)}
                      style={{ 
                        borderRadius: '10px',
                        background: '#f8f9fa',
                        border: 'none',
                        boxShadow: 'none'
                      }}
                    >
                      <option value="all">All Roles</option>
                      <option value="user">Users</option>
                      <option value="distributor">Distributors</option>
                      <option value="admin">Admins</option>
                    </select>
                  </div>
                  
                  <div className="col-lg-2 col-md-6 mb-3">
                    <label className="form-label fw-bold text-dark mb-2">Status Filter</label>
                    <select
                      className="form-select border-0"
                      value={statusFilter}
                      onChange={(e) => setStatusFilter(e.target.value)}
                      style={{ 
                        borderRadius: '10px',
                        background: '#f8f9fa',
                        border: 'none',
                        boxShadow: 'none'
                      }}
                    >
                      <option value="all">All Status</option>
                      <option value="active">Active</option>
                      <option value="inactive">Inactive</option>
                    </select>
                  </div>
                  
                  <div className="col-lg-2 col-md-6 mb-3">
                    <button
                      className="btn btn-outline-secondary w-100 rounded-pill py-2"
                      onClick={() => {
                        setSearchTerm('');
                        setRoleFilter('all');
                        setStatusFilter('all');
                      }}
                      style={{ 
                        borderWidth: '2px',
                        fontWeight: '600'
                      }}
                    >
                      Clear Filters
                    </button>
                  </div>
                  
                  <div className="col-lg-2 col-md-6 mb-3">
                    <div className="d-flex align-items-center justify-content-center h-100">
                      <span className="badge bg-primary fs-6 px-3 py-2 rounded-pill">
                        {filteredUsers.length} Users Found
                      </span>
                    </div>
                  </div>
                </div>
                
                {selectedUsers.length > 0 && (
                  <div className="row mt-3">
                    <div className="col-12">
                      <div className="d-flex gap-2 justify-content-center">
                        <button 
                          className="btn btn-success rounded-pill px-4 py-2"
                          onClick={() => handleBulkStatusUpdate(true)}
                          style={{ fontWeight: '600' }}
                        >
                          <PersonCheck className="me-2" />
                          Activate Selected ({selectedUsers.length})
                        </button>
                        <button 
                          className="btn btn-danger rounded-pill px-4 py-2"
                          onClick={() => handleBulkStatusUpdate(false)}
                          style={{ fontWeight: '600' }}
                        >
                          <PersonX className="me-2" />
                          Deactivate Selected ({selectedUsers.length})
                        </button>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Users Table */}
        <div className="row">
          <div className="col-12">
            <div className="card border-0 shadow-lg" style={{ 
              background: 'rgba(255, 255, 255, 0.95)', 
              backdropFilter: 'blur(10px)',
              borderRadius: '20px'
            }}>
              <div className="card-header border-0" style={{ 
                background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                borderRadius: '20px 20px 0 0'
              }}>
                <div className="d-flex justify-content-between align-items-center">
                  <div className="d-flex align-items-center">
                    <div className="bg-white bg-opacity-20 rounded-circle p-2 me-3">
                      <People className="text-white" size={24} />
                    </div>
                    <div>
                      <h5 className="mb-0 text-white fw-bold">Users List</h5>
                      <small className="text-white opacity-75">Manage all users and distributors</small>
                    </div>
                  </div>
                  <span className="badge bg-white text-primary fs-6 px-3 py-2 rounded-pill fw-bold">
                    {filteredUsers.length} Users
                  </span>
                </div>
              </div>
              
              <div className="card-body p-0">
                {loading ? (
                  <div className="text-center py-5">
                    <div className="spinner-border text-primary mb-3" role="status" style={{ width: '3rem', height: '3rem' }}>
                      <span className="visually-hidden">Loading...</span>
                    </div>
                    <h5 className="text-muted">Loading users...</h5>
                    <p className="text-muted">Please wait while we fetch the user data</p>
                  </div>
                ) : filteredUsers.length > 0 ? (
                  <div className="table-responsive">
                    <table className="table table-hover mb-0">
                      <thead style={{ background: 'linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%)' }}>
                        <tr>
                          <th className="fw-bold text-dark border-0 py-3 px-4">
                            <Form.Check
                              type="checkbox"
                              checked={selectedUsers.length === filteredUsers.length}
                              onChange={handleSelectAll}
                            />
                          </th>
                          <th 
                            className="fw-bold text-dark border-0 py-3 px-4" 
                            style={{ cursor: 'pointer', userSelect: 'none' }}
                            onClick={() => handleSort('name')}
                          >
                            User {sortBy === 'name' && (sortOrder === 'asc' ? '↑' : '↓')}
                          </th>
                          <th 
                            className="fw-bold text-dark border-0 py-3 px-4" 
                            style={{ cursor: 'pointer', userSelect: 'none' }}
                            onClick={() => handleSort('email')}
                          >
                            Email {sortBy === 'email' && (sortOrder === 'asc' ? '↑' : '↓')}
                          </th>
                          <th 
                            className="fw-bold text-dark border-0 py-3 px-4" 
                            style={{ cursor: 'pointer', userSelect: 'none' }}
                            onClick={() => handleSort('phone')}
                          >
                            Phone {sortBy === 'phone' && (sortOrder === 'asc' ? '↑' : '↓')}
                          </th>
                          <th 
                            className="fw-bold text-dark border-0 py-3 px-4" 
                            style={{ cursor: 'pointer', userSelect: 'none' }}
                            onClick={() => handleSort('role')}
                          >
                            Role {sortBy === 'role' && (sortOrder === 'asc' ? '↑' : '↓')}
                          </th>
                          <th 
                            className="fw-bold text-dark border-0 py-3 px-4" 
                            style={{ cursor: 'pointer', userSelect: 'none' }}
                            onClick={() => handleSort('wallet_balance')}
                          >
                            Wallet Balance {sortBy === 'wallet_balance' && (sortOrder === 'asc' ? '↑' : '↓')}
                          </th>
                          <th 
                            className="fw-bold text-dark border-0 py-3 px-4" 
                            style={{ cursor: 'pointer', userSelect: 'none' }}
                            onClick={() => handleSort('status')}
                          >
                            Status {sortBy === 'status' && (sortOrder === 'asc' ? '↑' : '↓')}
                          </th>
                          <th 
                            className="fw-bold text-dark border-0 py-3 px-4" 
                            style={{ cursor: 'pointer', userSelect: 'none' }}
                            onClick={() => handleSort('created_at')}
                          >
                            Joined {sortBy === 'created_at' && (sortOrder === 'asc' ? '↑' : '↓')}
                          </th>
                          <th className="fw-bold text-dark border-0 py-3 px-4">Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {filteredUsers.map((user, index) => (
                          <tr key={user.id} style={{ 
                            background: index % 2 === 0 ? 'rgba(248, 249, 250, 0.5)' : 'white',
                            transition: 'all 0.3s ease'
                          }}
                          onMouseEnter={(e) => {
                            e.currentTarget.style.background = 'rgba(102, 126, 234, 0.1)';
                            e.currentTarget.style.transform = 'scale(1.01)';
                          }}
                          onMouseLeave={(e) => {
                            e.currentTarget.style.background = index % 2 === 0 ? 'rgba(248, 249, 250, 0.5)' : 'white';
                            e.currentTarget.style.transform = 'scale(1)';
                          }}>
                            <td className="border-0 py-3 px-4">
                              <Form.Check
                                type="checkbox"
                                checked={selectedUsers.includes(user.id)}
                                onChange={() => handleSelectUser(user.id)}
                              />
                            </td>
                            <td className="border-0 py-3 px-4">
                              <div className="d-flex align-items-center">
                                {getRoleIcon(user.role)}
                                <div className="ms-2">
                                  <div className="fw-bold text-dark">{user.name}</div>
                                  <small className="text-muted">ID: {user.custom_id || user.id}</small>
                                </div>
                              </div>
                            </td>
                            <td className="border-0 py-3 px-4">
                              <span className="text-dark">{user.email}</span>
                            </td>
                            <td className="border-0 py-3 px-4">
                              <span className="text-dark">{user.phone || '-'}</span>
                            </td>
                            <td className="border-0 py-3 px-4">
                              {getRoleBadge(user.role)}
                            </td>
                            <td className="border-0 py-3 px-4">
                              <div className="d-flex align-items-center">
                                <CurrencyRupee className="text-success me-1" size={18} />
                                <span className="fw-bold text-success fs-6">
                                  {(user.wallet_balance || 0).toLocaleString()}
                                </span>
                              </div>
                            </td>
                            <td className="border-0 py-3 px-4">
                              {getStatusBadge(user.is_active)}
                            </td>
                            <td className="border-0 py-3 px-4">
                              <span className="text-dark">{new Date(user.created_at).toLocaleDateString()}</span>
                            </td>
                            <td className="border-0 py-3 px-4">
                              <div className="d-flex gap-2">
                                <button
                                  className={`btn btn-sm px-3 py-2 fw-bold ${
                                    user.is_active ? 'btn-danger' : 'btn-success'
                                  }`}
                                  onClick={() => handleStatusToggle(user.id, user.is_active)}
                                  title={user.is_active ? "Deactivate User" : "Activate User"}
                                  style={{ 
                                    transition: 'all 0.3s ease',
                                    border: 'none'
                                  }}
                                  onMouseEnter={(e) => {
                                    e.currentTarget.style.transform = 'scale(1.05)';
                                  }}
                                  onMouseLeave={(e) => {
                                    e.currentTarget.style.transform = 'scale(1)';
                                  }}
                                >
                                  {user.is_active ? "Deactivate" : "Activate"}
                                </button>
                                <button
                                  className="btn btn-outline-primary btn-sm px-3 py-2"
                                  onClick={() => openEditModal(user)}
                                  title="Edit User"
                                  style={{ 
                                    transition: 'all 0.3s ease',
                                    borderWidth: '2px'
                                  }}
                                  onMouseEnter={(e) => {
                                    e.currentTarget.style.transform = 'scale(1.1)';
                                    e.currentTarget.style.background = '#007bff';
                                    e.currentTarget.style.color = 'white';
                                  }}
                                  onMouseLeave={(e) => {
                                    e.currentTarget.style.transform = 'scale(1)';
                                    e.currentTarget.style.background = 'transparent';
                                    e.currentTarget.style.color = '#007bff';
                                  }}
                                >
                                  Edit
                                </button>
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                ) : (
                  <div className="text-center py-5">
                    <div className="bg-light rounded-circle d-inline-flex align-items-center justify-content-center mb-4" style={{ width: '80px', height: '80px' }}>
                      <People className="text-muted" size={40} />
                    </div>
                    <h4 className="text-muted mb-3">No Users Found</h4>
                    <p className="text-muted mb-4">No users match your current filters. Try adjusting your search criteria.</p>
                    <button
                      className="btn btn-primary rounded-pill px-4 py-2"
                      onClick={() => {
                        setSearchTerm('');
                        setRoleFilter('all');
                        setStatusFilter('all');
                      }}
                    >
                      Clear All Filters
                    </button>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Create User Modal */}
      <Modal show={showCreateModal} onHide={() => setShowCreateModal(false)} size="md">
        <Modal.Header closeButton>
          <Modal.Title className="fw-bold text-success">Create New User</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form onSubmit={handleCreateUser}>
            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Name *</Form.Label>
                  <Form.Control
                    type="text"
                    value={formData.name}
                    onChange={(e) => setFormData({...formData, name: e.target.value})}
                    required
                  />
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Email *</Form.Label>
                  <Form.Control
                    type="email"
                    value={formData.email}
                    onChange={(e) => setFormData({...formData, email: e.target.value})}
                    required
                  />
                </Form.Group>
              </Col>
            </Row>
            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Password *</Form.Label>
                  <Form.Control
                    type="password"
                    value={formData.password}
                    onChange={(e) => setFormData({...formData, password: e.target.value})}
                    required
                  />
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Phone</Form.Label>
                  <Form.Control
                    type="text"
                    value={formData.phone}
                    onChange={(e) => setFormData({...formData, phone: e.target.value})}
                  />
                </Form.Group>
              </Col>
            </Row>
            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Role *</Form.Label>
                  <Form.Select
                    value={formData.role}
                    onChange={(e) => setFormData({...formData, role: e.target.value})}
                  >
                    <option value="user">User</option>
                    <option value="distributor">Distributor</option>
                    <option value="admin">Admin</option>
                  </Form.Select>
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Status</Form.Label>
                  <Form.Select
                    value={formData.is_active}
                    onChange={(e) => setFormData({...formData, is_active: e.target.value === 'true'})}
                  >
                    <option value={true}>Active</option>
                    <option value={false}>Inactive</option>
                  </Form.Select>
                </Form.Group>
              </Col>
            </Row>
            <div className="d-flex justify-content-end gap-2">
              <Button variant="secondary" onClick={() => setShowCreateModal(false)}>
                Cancel
              </Button>
              <Button variant="success" type="submit">
                Create User
              </Button>
            </div>
          </Form>
        </Modal.Body>
      </Modal>

      {/* Edit User Modal */}
      <Modal show={showEditModal} onHide={() => setShowEditModal(false)} size="md">
        <Modal.Header closeButton>
          <Modal.Title className="fw-bold text-primary">Edit User</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form onSubmit={handleEditUser}>
            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Name *</Form.Label>
                  <Form.Control
                    type="text"
                    value={formData.name}
                    onChange={(e) => setFormData({...formData, name: e.target.value})}
                    required
                  />
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Email *</Form.Label>
                  <Form.Control
                    type="email"
                    value={formData.email}
                    onChange={(e) => setFormData({...formData, email: e.target.value})}
                    required
                  />
                </Form.Group>
              </Col>
            </Row>
            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Password (leave blank to keep current)</Form.Label>
                  <Form.Control
                    type="password"
                    value={formData.password}
                    onChange={(e) => setFormData({...formData, password: e.target.value})}
                  />
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Phone</Form.Label>
                  <Form.Control
                    type="text"
                    value={formData.phone}
                    onChange={(e) => setFormData({...formData, phone: e.target.value})}
                  />
                </Form.Group>
              </Col>
            </Row>
            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Role *</Form.Label>
                  <Form.Select
                    value={formData.role}
                    onChange={(e) => setFormData({...formData, role: e.target.value})}
                  >
                    <option value="user">User</option>
                    <option value="distributor">Distributor</option>
                    <option value="admin">Admin</option>
                  </Form.Select>
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Status</Form.Label>
                  <Form.Select
                    value={formData.is_active}
                    onChange={(e) => setFormData({...formData, is_active: e.target.value === 'true'})}
                  >
                    <option value={true}>Active</option>
                    <option value={false}>Inactive</option>
                  </Form.Select>
                </Form.Group>
              </Col>
            </Row>
            <div className="d-flex justify-content-end gap-2">
              <Button variant="secondary" onClick={() => setShowEditModal(false)}>
                Cancel
              </Button>
              <Button variant="primary" type="submit">
                Update User
              </Button>
            </div>
          </Form>
        </Modal.Body>
      </Modal>
      </div>
    </>
  );
};

export default UserManagement;
